import { recordIdentifierFor } from "@warp-drive/core";
import { buildBaseURL, buildQueryParams } from "@warp-drive/utilities";
import type { QueryParamsSource } from "@warp-drive/core/types/params";
import { SkipCache, type CacheOptions } from "@warp-drive/core/types/request";
import { withReactiveResponse, withResponseType } from "@warp-drive/core/request";

type RequestOptions = Record<string, unknown> & {
  handler: { type: string };
};

type MutationOptions = RequestOptions & {
  record: unknown;
  listsToPatch?: unknown[];
};

const buildURL = (resourcePath: string, params?: QueryParamsSource) => {
  const url = URL.canParse(resourcePath) ? resourcePath : buildBaseURL({ resourcePath });
  return params ? `${url}?${buildQueryParams(params)}` : url;
};

const buildRequest = (method: "GET" | "POST", resourcePath: string, params?: QueryParamsSource, body?: unknown) => ({
  url: buildURL(resourcePath, params),
  method,
  ...(body !== undefined && { body: JSON.stringify(body) }),
});

const mutation = <T>(
  op: "createRecord" | "updateRecord" | "deleteRecord",
  resourcePath: string,
  params: QueryParamsSource | undefined,
  { record, ...options }: MutationOptions,
  body?: unknown,
) =>
  withReactiveResponse<T>({
    ...buildRequest("POST", resourcePath, params, body),
    op,
    records: [recordIdentifierFor(record)],
    options,
  });

export function query<T>(
  resourcePath: string,
  params: QueryParamsSource | undefined,
  options: RequestOptions,
  cacheOptions?: CacheOptions,
) {
  return withReactiveResponse<T[]>({
    ...buildRequest("GET", resourcePath, params),
    op: "query",
    cacheOptions,
    options,
  });
}

export const createRecord = <T>(resourcePath: string, body: unknown, options: MutationOptions) =>
  mutation<T>("createRecord", resourcePath, undefined, options, body);

export const updateRecord = <T>(resourcePath: string, body: unknown, options: MutationOptions) =>
  mutation<T>("updateRecord", resourcePath, undefined, options, body);

export const deleteRecord = <T>(
  resourcePath: string,
  params: QueryParamsSource | undefined,
  options: MutationOptions,
) => mutation<T>("deleteRecord", resourcePath, params, options);

export const get = <T>(resourcePath: string, params?: QueryParamsSource) =>
  withResponseType<T>({
    ...buildRequest("GET", resourcePath, params),
    cacheOptions: { [SkipCache]: true },
  });

export const post = <T>(resourcePath: string, body?: unknown) =>
  withResponseType<T>({
    ...buildRequest("POST", resourcePath, undefined, body),
    cacheOptions: { [SkipCache]: true },
  });
